import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarCheck2,
  CalendarClock,
  CheckCircle2,
  MapPin,
  SearchX,
  Users,
  WifiOff,
  XCircle,
} from 'lucide-react';
import {
  zonedDateOf,
  type PublicBookingResult,
  type PublicLocation,
  type PublicService,
} from '@rx-manager/shared';
import { bookingApi, BookingApiError } from '../lib/api';
import { useI18n, type MessageKey } from '../lib/i18n';
import { Alert, Button, Card, Field, HEADING, IconTile, Input, Spinner } from '../components/ui';
import { AppointmentHead, Detail } from '../components/AppointmentCard';
import { problemKind, type ProblemKind } from '../App';
import { TimeStep } from './BookingFlow';
import { FormsDue } from './forms/FormsDue';
import { ManageCheckIn } from './check-in/ManageCheckIn';

/** See `BookingFlow`: a tagged pair, because `MessageKey | string` is `string`. */
type Failure = { key: MessageKey } | { text: string };

/**
 * "Manage my appointment", reached by the signed token in a confirmation.
 *
 * Not under a pharmacy slug: the token already names its pharmacy, and taking
 * the slug as well would invite a mismatch nobody would think to check. It is
 * also why `manage` is a reserved slug — a pharmacy claiming it would shadow
 * every one of these links.
 *
 * Change time and Cancel are both here (FE-B9-06). Change time reuses the
 * booking flow's own time step, then holds the new time and applies the
 * existing `bookingApi.reschedule` — so the move faces the same checks a fresh
 * booking does. It needs the pharmacy, service and branch: the confirmation's
 * link supplies them (`?p&s&l`), and the manage response does where the link
 * carries none, as an emailed one does. Without either — an older backend — the
 * page says to call the pharmacy.
 */
export function ManagePage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const { t, locale } = useI18n();

  const [booking, setBooking] = useState<PublicBookingResult | null>(null);
  const linked = {
    slug: params.get('p') ?? '',
    serviceId: params.get('s') ?? '',
    locationId: params.get('l') ?? '',
  };
  const context =
    linked.slug && linked.serviceId && linked.locationId ? linked : (booking?.context ?? linked);
  const hasContext = Boolean(context.slug && context.serviceId && context.locationId);

  const [state, setState] = useState<'loading' | 'ready' | ProblemKind>('loading');
  const [confirming, setConfirming] = useState(false);
  /** Track CGS — the one person of a party being cancelled, by position; null is everyone. */
  const [cancelling, setCancelling] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [moved, setMoved] = useState(false);
  /** The service and branch to pick a new time for, once Change time is pressed. */
  const [picking, setPicking] = useState<{
    service: PublicService;
    location: PublicLocation;
    phone: string | null;
  } | null>(null);
  const [newDate, setNewDate] = useState('');
  const [newStart, setNewStart] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** Track CGS (CGS-D14) — a party moves everyone still on its time together; one person is 1. */
  const movingParty = booking?.party
    ? booking.party.people.filter(
        (person) => person.together && (person.status === 'booked' || person.status === 'checked_in'),
      ).length
    : 1;

  useEffect(() => {
    let cancelled = false;
    void bookingApi
      .managed(token)
      .then((result) => {
        if (cancelled) return;
        setBooking(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(problemKind(error));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (state !== 'loading') headingRef.current?.focus();
  }, [state]);

  const when = useMemo(() => {
    if (!booking) return '';
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: booking.location.timezone,
    }).format(new Date(booking.startAt));
  }, [booking, locale]);

  const cancel = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      // Track CGS (CGS-D14) — on a party's link: one person, or everyone.
      const target = booking?.party
        ? cancelling !== null
          ? { person: cancelling }
          : { all: true }
        : undefined;
      setBooking(await bookingApi.cancel(token, reason.trim() || undefined, target));
      setConfirming(false);
      setCancelling(null);
      setReason('');
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setBusy(false);
    }
  };

  const startPicking = async (): Promise<void> => {
    if (!booking) return;
    setBusy(true);
    setFailure(null);
    setMoved(false);
    try {
      // Track CGS — a clinic visit's times are on the clinic's own page (CGS-D3).
      const page = booking.location.clinicCode
        ? await bookingApi.clinicPage(context.slug, booking.location.clinicCode)
        : await bookingApi.page(context.slug);
      const service = page.services.find((entry) => entry.id === context.serviceId);
      const location = page.locations.find((entry) => entry.id === context.locationId);
      if (!service || !location) {
        setFailure({ key: 'manage.noReschedule' });
        return;
      }
      setPicking({ service, location, phone: location.phone ?? page.profile.supportPhone });
      setNewDate(zonedDateOf(new Date(booking.startAt), location.timezone));
      setNewStart(null);
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setBusy(false);
    }
  };

  /** Hold the new time, then move the appointment onto it. A refused move gives the hold back. */
  const changeTime = async (): Promise<void> => {
    if (!picking || !newStart) return;
    setBusy(true);
    setFailure(null);
    let holdToken: string | null = null;
    try {
      const held = await bookingApi.hold(context.slug, {
        serviceId: picking.service.id,
        locationId: picking.location.id,
        startAt: newStart,
        // Track CGS (CGS-D14) — a party's hold for everyone still together: the move takes them all.
        ...(movingParty > 1 ? { partySize: movingParty } : {}),
      });
      holdToken = held.holdToken;
      setBooking(await bookingApi.reschedule(token, held.holdToken));
      holdToken = null;
      setPicking(null);
      setMoved(true);
    } catch (error) {
      if (holdToken) {
        void bookingApi.releaseHold(context.slug, holdToken).catch(() => undefined);
      }
      setFailure(failureOf(error));
      setNewStart(null);
    } finally {
      setBusy(false);
    }
  };

  if (state === 'loading') return <Spinner label={t('time.loading')} />;

  if (state !== 'ready') {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <IconTile
          icon={state === 'missing' ? SearchX : state === 'offline' ? WifiOff : AlertTriangle}
          tone={state === 'missing' ? 'muted' : 'warning'}
          size="lg"
        />
        <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
          {t(
            state === 'missing'
              ? 'error.missing'
              : state === 'offline'
                ? 'error.offline'
                : 'error.unknown',
          )}
        </h1>
        {state === 'missing' && (
          <p className="max-w-md text-base text-muted-foreground">{t('error.missingHelp')}</p>
        )}
        {state !== 'missing' && (
          <Button onClick={() => window.location.reload()}>{t('error.retry')}</Button>
        )}
      </div>
    );
  }

  if (!booking) return null;

  const isOver =
    booking.status === 'cancelled' ||
    booking.status === 'completed' ||
    booking.status === 'no_show';
  /** Track CGS — the party's people still booked, and those still on its time. */
  const party = booking.party;
  const liveInParty = (party?.people ?? []).filter(
    (person) => person.status === 'booked' || person.status === 'checked_in',
  );
  const partyOver = Boolean(party) && liveInParty.length === 0;
  /** Nothing left to change: the visit, or (for a party) everyone in it. */
  const over = party ? partyOver : isOver;
  const cancellingName =
    cancelling !== null ? party?.people.find((person) => person.position === cancelling)?.firstName : undefined;

  return (
    <div className="space-y-6">
      <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
        {t('manage.heading')}
      </h1>

      {failure && <Alert>{'key' in failure ? t(failure.key) : failure.text}</Alert>}

      {(party ? partyOver : booking.status === 'cancelled') && (
        <p
          role="status"
          className="flex items-center gap-3 rounded-xl border border-danger/30 bg-danger/5 p-4 text-base font-medium text-heading"
        >
          <XCircle className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
          {party ? t('manage.partyCancelled') : t('manage.cancelled')}
        </p>
      )}
      {booking.status === 'completed' && (
        <p role="status" className="flex items-center gap-3 rounded-xl bg-muted/70 p-4 text-base font-medium text-heading">
          <CalendarCheck2 className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          {t('manage.completed')}
        </p>
      )}

      {moved && (
        <p role="status" className="flex items-center gap-3 rounded-xl border border-success/30 bg-success/5 p-4 text-base font-medium text-heading">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
          {t('manage.moved', { when })}
        </p>
      )}

      {/* When, What, Where — in that order, as one description list's terms read. */}
      <div className="overflow-hidden rounded-xl border border-border">
        <AppointmentHead
          iso={booking.startAt}
          timeZone={booking.location.timezone}
          first={{ label: t('done.when'), value: when }}
          second={{ label: t('done.what'), value: booking.serviceTitle }}
        />
        <dl className="px-4 sm:px-5">
          <Detail
            icon={MapPin}
            label={t('done.where')}
            value={[booking.location.name, booking.location.address]
              .filter(Boolean)
              .join(' · ')}
          />
        </dl>
      </div>

      {/* Track CGS (CGS-D14) — everyone in the booking, each with their own status. */}
      {party && (
        <section aria-labelledby="party-heading" className="space-y-3">
          <h2 id="party-heading" className="flex items-center gap-2 text-lg font-semibold text-heading">
            <Users className="h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
            {t('manage.partyHeading', { count: party.people.length })}
          </h2>
          <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
            {party.people.map((person) => {
              const live = person.status === 'booked' || person.status === 'checked_in';
              return (
                <li key={person.position} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-heading">
                      {person.firstName} {person.lastName}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {person.status === 'cancelled'
                        ? t('manage.personCancelled')
                        : !person.together
                          ? t('manage.personMoved')
                          : t('manage.personBooked')}
                    </p>
                  </div>
                  {live && liveInParty.length > 1 && !confirming && !picking && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCancelling(person.position);
                        setConfirming(true);
                      }}
                    >
                      {t('manage.cancelPerson', { name: person.firstName })}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/*
        Track CIQ (CIQ-D6, D39) — "I'm here", for an in-person visit today while check-in is on.
        Feature-detected: an answer without `checkIn` (an older backend) shows nothing of it.
      */}
      {booking.checkIn && booking.status !== 'cancelled' && booking.status !== 'completed' && (
        <ManageCheckIn token={token} booking={booking} checkIn={booking.checkIn} onBooking={setBooking} />
      )}

      {/* Track SFM: no session here — anyone holding the email can open this page. */}
      {!over && booking.forms && <FormsDue forms={booking.forms} />}

      {!over && picking && (
        <section aria-labelledby="reschedule-heading" className="space-y-5 border-t border-border pt-6">
          <h2 id="reschedule-heading" className="text-xl font-semibold text-heading">
            {t('manage.rescheduleHeading')}
          </h2>
          <TimeStep
            slug={context.slug}
            service={picking.service}
            location={picking.location}
            date={newDate}
            onDate={setNewDate}
            selected={newStart}
            onSelect={setNewStart}
            onContinue={() => void changeTime()}
            onBack={() => setPicking(null)}
            busy={busy}
            phone={picking.phone}
            backLabel={t('manage.keepTime')}
            continueLabel={t('manage.useThisTime')}
          />
        </section>
      )}

      {!over && !picking && (
        <div className="space-y-4">
          {(!booking.rescheduleAllowed || !hasContext) && (
            <p className="rounded-xl bg-muted/70 p-4 text-base text-foreground">
              {t('manage.noReschedule')}
              {booking.location.phone && ` ${booking.location.phone}`}
            </p>
          )}

          {/* Track CIQ (CIQ-D6) — a checked-in visit isn't moved: the server refuses it (409). */}
          {booking.rescheduleAllowed &&
            hasContext &&
            !confirming &&
            (!party || movingParty > 0) &&
            booking.status !== 'checked_in' && (
              <Button loading={busy} onClick={() => void startPicking()}>
                <CalendarClock className="h-4 w-4" aria-hidden="true" />
                {party ? t('manage.moveAll') : t('manage.reschedule')}
              </Button>
            )}

          {confirming ? (
            <Card tone="muted" className="space-y-4">
              <p className="text-base font-medium text-heading">
                {cancellingName
                  ? t('manage.cancelPersonConfirm', { name: cancellingName })
                  : party
                    ? t('manage.cancelAllConfirm')
                    : t('manage.cancelConfirm')}
              </p>
              <Field id="cancel-reason" label={t('manage.cancelReason')}>
                {(props) => (
                  <Input
                    {...props}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                )}
              </Field>
              <div className="flex flex-wrap gap-3">
                <Button variant="danger" loading={busy} onClick={() => void cancel()}>
                  {cancellingName
                    ? t('manage.cancelPerson', { name: cancellingName })
                    : party
                      ? t('manage.cancelAll')
                      : t('manage.cancel')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setConfirming(false);
                    setCancelling(null);
                  }}
                >
                  {t('manage.keepIt')}
                </Button>
              </div>
            </Card>
          ) : (
            <Button
              variant="outline"
              onClick={() => {
                setCancelling(null);
                setConfirming(true);
              }}
            >
              <XCircle className="h-4 w-4" aria-hidden="true" />
              {party ? t('manage.cancelAll') : t('manage.cancel')}
            </Button>
          )}
        </div>
      )}

      {!over && <p className="text-sm text-muted-foreground">{t('done.saveThis')}</p>}
    </div>
  );
}

function failureOf(error: unknown): Failure {
  if (!(error instanceof BookingApiError)) return { key: 'error.unknown' };
  if (error.status === 0) return { key: 'error.offline' };
  if (error.isSlotGone) return { key: 'error.slotGone' };
  return error.message !== 'unknown' ? { text: error.message } : { key: 'error.unknown' };
}
